import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Contact } from '@/lib/contacts/types'

interface NewContactForm {
  name: string
  email: string
}

export function DonationContactField({
  contacts,
  contactsLoading,
  createdContact,
  creatingContact,
  createContactError,
  createContactPending,
  error,
  newContact,
  onAddContact,
  onSelectContact,
  selectedContactId,
  setCreatingContact,
  setNewContact,
}: {
  contacts: Contact[]
  contactsLoading: boolean
  createdContact: Contact | null
  creatingContact: boolean
  createContactError: Error | null
  createContactPending: boolean
  error?: string
  newContact: NewContactForm
  onAddContact: () => void
  onSelectContact: (contactId: string) => void
  selectedContactId: string
  setCreatingContact: React.Dispatch<React.SetStateAction<boolean>>
  setNewContact: React.Dispatch<React.SetStateAction<NewContactForm>>
}) {
  return (
    <div className="sm:col-span-2">
      <FormField label="Contact" error={error}>
        {creatingContact ? (
          <InlineContactCreate
            error={createContactError}
            form={newContact}
            pending={createContactPending}
            onAdd={onAddContact}
            onCancel={() => setCreatingContact(false)}
            setForm={setNewContact}
          />
        ) : (
          <>
            <Select
              value={selectedContactId}
              onValueChange={onSelectContact}
              disabled={contactsLoading}
            >
              <SelectTrigger aria-label="Contact" className="h-8">
                <SelectValue
                  placeholder={contactsLoading ? 'Loading Contacts' : 'Select Contact'}
                />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={String(contact.id)}>
                    {contact.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {createdContact && selectedContactId === String(createdContact.id) && (
              <p className="text-caption text-muted-foreground">{createdContact.name} selected</p>
            )}
            <Button
              type="button"
              variant="link"
              size="inline"
              onClick={() => setCreatingContact(true)}
            >
              Create new contact
            </Button>
          </>
        )}
      </FormField>
    </div>
  )
}

function InlineContactCreate({
  error,
  form,
  pending,
  onAdd,
  onCancel,
  setForm,
}: {
  error: Error | null
  form: NewContactForm
  pending: boolean
  onAdd: () => void
  onCancel: () => void
  setForm: React.Dispatch<React.SetStateAction<NewContactForm>>
}) {
  return (
    <div className="grid gap-3 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-2">
      <FormField
        label="Name"
        htmlFor="new-donor-name"
        required
        error={apiFieldMessages(error, 'name')[0]}
      >
        <Input
          id="new-donor-name"
          aria-label="New contact name"
          className="h-8"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        />
      </FormField>
      <FormField
        label="Email"
        htmlFor="new-donor-email"
        error={apiFieldMessages(error, 'email')[0]}
      >
        <Input
          id="new-donor-email"
          aria-label="New contact email"
          className="h-8"
          value={form.email}
          onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
        />
      </FormField>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" variant="ghost" className="h-8" onClick={onCancel}>
          Cancel new contact
        </Button>
        <Button type="button" className="h-8" disabled={pending} onClick={onAdd}>
          {pending ? 'Adding...' : 'Add contact'}
        </Button>
      </div>
    </div>
  )
}
